import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import { Screen } from "@/components/Layout";
import { Spinner } from "@/components/Controls";
import { colors, spacing, radius, fonts, type } from "@/theme";
import { createUser, setStoredUserId } from "@/api";

export default function Onboarding() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [about, setAbout] = useState("");
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canStart = firstName.trim().length > 0;

  const handleStart = async () => {
    if (!canStart || loading) return;
    setLoading(true);
    setError("");
    try {
      const user = await createUser({
        first_name: firstName.trim(),
        about: about.trim() || null,
        family_contact: contact.trim() || null,
      });
      setStoredUserId(user.id);
      navigate("/voice-setup");
    } catch {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Screen testID="onboarding-screen">
      <div style={{ padding: `${spacing["2xl"]}px ${spacing.xl}px 160px` }}>
        <h1 data-testid="onboarding-heading" style={{ ...fonts.displayBold, fontSize: type["3xl"], color: colors.onSurface, lineHeight: 1.15, margin: 0 }}>
          Let&rsquo;s get<br />acquainted
        </h1>
        <p style={{ ...fonts.text, fontSize: type.lg, color: colors.muted, marginTop: spacing.md, marginBottom: spacing["2xl"] }}>
          Just your first name to begin — no account, no passwords, ever.
        </p>

        <Field label="Your first name">
          <input data-testid="onboarding-name-input" style={inputStyle} value={firstName}
            onChange={(e) => setFirstName(e.target.value)} placeholder="Your first name" />
        </Field>
        <Field label="What's on your mind? (optional)">
          <textarea data-testid="onboarding-about-input" style={{ ...inputStyle, minHeight: 110, paddingTop: spacing.lg }}
            value={about} onChange={(e) => setAbout(e.target.value)} placeholder="A little about what brings you here — or leave this blank" />
        </Field>
        <Field label="Trusted contact (optional)">
          <input data-testid="onboarding-contact-input" style={inputStyle} value={contact}
            onChange={(e) => setContact(e.target.value)} placeholder="Someone we could reach in an emergency" />
        </Field>

        {error && <div data-testid="onboarding-error" style={{ ...fonts.textSemiBold, fontSize: type.base, color: colors.error, marginTop: spacing.sm }}>{error}</div>}

        <p data-testid="onboarding-disclaimer" style={{ ...fonts.text, fontSize: type.base, color: colors.muted, lineHeight: 1.4, marginTop: spacing.lg }}>
          Ember is a warm AI voice — not a real person, and not a source of medical, legal, or financial advice.
        </p>
      </div>

      <div style={footerStyle}>
        <button data-testid="onboarding-start-button" onClick={handleStart} disabled={!canStart || loading}
          className="ember-press" style={{ ...ctaStyle, background: canStart ? colors.brandPrimary : colors.border }}>
          {loading ? <Spinner color={colors.onBrandPrimary} /> : (
            <>
              <span style={{ ...fonts.textExtraBold, fontSize: type.xl, color: colors.onBrandPrimary }}>Start talking</span>
              <Icon name="arrow-right" size={26} color={colors.onBrandPrimary} />
            </>
          )}
        </button>
      </div>
    </Screen>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: spacing.xl }}>
      <div style={{ ...fonts.textBold, fontSize: type.lg, color: colors.onSurface, marginBottom: spacing.sm }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", minHeight: 72, background: colors.surfaceSecondary, borderRadius: radius.lg,
  border: `2px solid ${colors.border}`, padding: `0 ${spacing.lg}px`, ...fonts.textSemiBold,
  fontSize: type.xl, color: colors.onSurface,
};
const footerStyle = {
  position: "sticky", bottom: 0, padding: `${spacing.md}px ${spacing.xl}px ${spacing.xl}px`, background: colors.surface,
};
const ctaStyle = {
  width: "100%", minHeight: 76, borderRadius: radius.lg, display: "flex", alignItems: "center",
  justifyContent: "center", gap: spacing.md,
};
